import {
  MutationCache, QueryCache, QueryClient, matchQuery, type Mutation, type Query,
} from '@tanstack/react-query';
import type { ConnectError } from '@square/web-rpc-client/utils';
import { getEnvironment, isHeadlessBrowser } from '@squareup/util-environment';
import { isBrowser } from '@squareup/util-isomorphic';
import type { Environment } from './types';
import { initDashboardQueryContext } from './context';

const DEFAULT_QUERY_RETRIES = 3;
const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 404]);

export interface ErrorContext {
  responseStatusCode: number | null;
  [key: string]: unknown;
}

export interface QueryErrorContext extends ErrorContext {
  query: {
    queryHash: string;
  };
}

export type DashboardQueryInitOptions = {

  /**
   * The base URL to use for the query client.
   * Setting this will cause requests configured with relative paths
   * to be sent to the baseUrl you provide.
   *
   * @default ""
   */
  baseUrl?: string;

  /**
   * Return false to prevent a retry, or true to force a retry.
   * Return null or nothing to delegate the choice to default internal checks (recommended).
   *
   * @example
   * ```ts
   * initDashboardQueryClient({
   *   beforeRetry: () => {
   *     if (fooCondition) {
   *       return false; // Don't retry if fooCondition is true
   *     }
   *   },
   * });
   * ```
   */
  beforeRetry?: (count: number, error: unknown) => boolean | null | undefined | void;

  /**
   * The environment to use for the query client.
   *
   * @default 'production'
   */
  environment?: Environment;

  /**
   * A function to call when a query error occurs. Useful for e.g. logging issues to Sentry.
   *
   * @example
   * ```ts
   * onQueryError: ({ error, context }) => {
   *   Sentry.withScope((scope) => {
   *     scope.setExtras(context);
   *     Sentry.captureException(error);
   *   });
   * }
   * ```
   */
  onQueryError?: (args: { error: unknown; context: QueryErrorContext; query: Query<unknown, unknown> }) => void;

  /**
   * A function to call when a query error occurs. Useful for e.g. logging issues to Sentry.
   *
   * @example
   * ```ts
   * onMutationError: ({ error, context }) => {
   *   Sentry.withScope((scope) => {
   *     scope.setExtras(context);
   *     Sentry.captureException(error);
   *   });
   * }
   * ```
   */
  onMutationError?: (args: { error: unknown; context: ErrorContext; mutation: Mutation<unknown, unknown> }) => void;

  /**
   * Default stale time for queries (time before data is considered stale).
   * Individual queries can override this value.
   *
   * @default 600000 (10 minutes)
   *
   * @example
   * ```ts
   * // Banking MFE wants fresh data on each visit
   * initDashboardQueryClient({
   *   staleTime: 0,  // Always refetch
   * });
   * ```
   */
  staleTime?: number;

  /**
   * Default garbage collection time (time before unused cached data is removed).
   * Individual queries can override this value.
   *
   * @default 600000 (10 minutes)
   *
   * @example
   * ```ts
   * initDashboardQueryClient({
   *   gcTime: 0,  // Don't cache data
   * });
   * ```
   */
  gcTime?: number;
};

export const initDashboardQueryClient = (
  {
    beforeRetry,
    onQueryError,
    onMutationError,
    environment = getEnvironment(),
    baseUrl = '',
    staleTime = 1000 * 60 * 10,
    gcTime = 1000 * 60 * 10,
  }: DashboardQueryInitOptions = {},
) => {
  initDashboardQueryContext({
    baseUrl,
    environment,
    loggingEnabled: isBrowser() && shouldEnableLogging({
      environment,
      searchParams: new URLSearchParams(typeof window !== 'undefined' ? window.location.search : ''),
    }),
  });

  const client: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: isTestEnvironment(environment) ? 0 : staleTime,
        gcTime: isTestEnvironment(environment) ? 0 : gcTime,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        throwOnError: false,
        retry: (count, error) => {
          if (beforeRetry) {
            const beforeRetryResult = beforeRetry(count, error);
            if (typeof beforeRetryResult === 'boolean') {
              return beforeRetryResult;
            }
          }

          if (isTestEnvironment(environment)) {
            return false;
          }

          const statusCode = getErrorStatusCode(error);
          if (statusCode && NON_RETRYABLE_STATUS_CODES.has(statusCode)) {
            return false;
          }

          return count < DEFAULT_QUERY_RETRIES;
        },
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        const statusCode = getErrorStatusCode(error);

        const context: QueryErrorContext = {
          responseStatusCode: statusCode,
          query: { queryHash: query.queryHash },
          ...(query.meta?.errorContext as Record<string, unknown>),
        };

        if (onQueryError) {
          onQueryError({ error, context, query });
        }
      },
    }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => client.invalidateQueries({
        predicate: (query: Query) => (
          Array.isArray(mutation.meta?.invalidates) &&
            mutation.meta.invalidates.some((queryKey) => matchQuery({ queryKey }, query))
        ),
      }),
      onError: (error, _variables, _context, mutation) => {
        const statusCode = getErrorStatusCode(error);

        const context: ErrorContext = {
          responseStatusCode: statusCode,
          ...(mutation.meta?.errorContext as Record<string, unknown>),
        };

        if (onMutationError) {
          onMutationError({ error, context, mutation });
        }
      },
    }),
  });

  return client;
};

function getErrorStatusCode(error: unknown): number | null {
  if (error && typeof error === 'object' && 'cause' in error) {
    const errorObj = error as { cause: unknown };
    return getErrorStatusCode(errorObj.cause);
  }

  if (isConnectError(error)) {
    return error.rawResponse.status;
  }
  if (isRpcError(error)) {
    return error.responseStatus;
  }

  return null;
}

function isConnectError(error: unknown): error is ConnectError {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return 'rawResponse' in error;
}

// TODO(SQWP-896): we don't have a shared type for RpcError like we do in Dashboard yet,
// so we just assert that the error has the responseStatus field we care about here.
function isRpcError(error: unknown): error is { responseStatus: number } {
  if (!error || typeof error !== 'object') {
    return false;
  }
  return 'responseStatus' in error;
}

function isTestEnvironment(environment: Environment): environment is 'test' | 'testing' {
  return environment === 'test' || environment === 'testing';
}

function shouldEnableLogging({ environment, searchParams }: { environment: Environment; searchParams: URLSearchParams }): boolean {
  const logRPCFlag = getSearchParamFlag(searchParams, 'logrpc');

  if (typeof logRPCFlag === 'boolean') {
    return logRPCFlag;
  }

  return !isHeadlessBrowser() && (environment === 'development' || environment === 'staging');
}

// TODO(SQWP-894): move to util-url package
function getSearchParamFlag(search: URLSearchParams, flag: string): boolean | null {
  switch (search.get(flag)) {
    case 'true':
    case '1':
      return true;
    case 'false':
    case '0':
      return false;
    default:
      return null;
  }
}
